import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAnalytics } from '@bonusly/analytics';
import { emit } from '@bonusly/events';
import { useHttpClient } from '@bonusly/http-client';
import { useCurrentCompany, useCurrentUser, type CollectibleInventoryItem } from '@bonusly/who-am-i';

import { DEFAULT_GIFT_WRAP_USES } from '../../../config/recognition';
import { useDialogControl } from '../../../lib/dialog';
import { PROVIDER_CONFIG, type GifProvider } from '../../../lib/gif-provider';
import { usePostWrapReward } from '../../../queries/rewards';
import { useUser } from '../../../queries/user';
import type { UserDetail } from '../../../types/user';
import { titleCase } from '../../../utils/string';
import { Gif } from '../../action-input/lib/entities';
import type {
	BizyDoodleUpload,
	BizyGifSearch,
	BizyGifSearchResult,
	BizyGiveBoxDraft,
	BizyLinkHandler,
	BizyResolvedUser,
	BizyTrackFn,
	BizyUserResolver,
	BizyWrapInfo,
} from '../../bizy/renderers';
import { getActiveCollectibleItem } from '../../post-wrap/collectible-inventory';
import { getCollectibleImageUrl } from '../../post-wrap/post-wrap-images';
import { GiveBoxEvent } from '../../recognition/give-box/events';
import { createDraftContent } from '../../recognition/give-box/utils/content';

/**
 * Builds the per-row display data the bizy-kit cards (meeting_people_card,
 * person_picker) need from a Bonusly user id. Implemented as a custom hook —
 * the package calls it once per card subcomponent, so it can safely use
 * `useUser` (single-user fetch). Falls back to a blank shape while loading or
 * when the id resolves to nothing.
 */
export function useBizyKitUserResolver(id?: string): BizyResolvedUser {
	const { data, isLoading } = useUser(id);
	return {
		displayName: data?.displayName,
		avatarUrl: data?.profilePic,
		subtitle: buildSubtitle(data),
		isLoading,
	};
}

// Mirrors the backend's `extract_title` fallback chain (job_title → title →
// role). Display-only; kept here so no derived title is persisted.
function resolveTitle(customProperties: Record<string, string> | undefined): string | undefined {
	if (!customProperties) return undefined;
	return ['job_title', 'title', 'role'].map((key) => customProperties[key]?.trim()).find((value): value is string => !!value);
}

// Secondary line for the person-picker: "Title · Department", title-cased,
// falling back to the @username when no custom properties are set.
function buildSubtitle(user: UserDetail | undefined): string | undefined {
	if (!user) return undefined;
	const detail = [resolveTitle(user.customProperties), user.customProperties?.department?.trim()].filter((value): value is string => !!value).map(titleCase);
	if (detail.length > 0) return detail.join(' \u00b7 ');
	return user.username ? `@${user.username}` : undefined;
}

// Accepts an absolute http(s) URL or a root-relative path (`/uploads/...`).
// Rejects protocol-relative (`//host`), `javascript:`, `data:`, and other
// schemes that shouldn't reach an <img src>.
function isSafeImageUrl(url: string): boolean {
	if (url.startsWith('/') && !url.startsWith('//')) return true;
	try {
		const { protocol } = new URL(url);
		return protocol === 'http:' || protocol === 'https:';
	} catch {
		return false;
	}
}

interface BizyKitReadOnlyRendererProps {
	onLinkClick: BizyLinkHandler;
	track: BizyTrackFn;
	resolveUser: BizyUserResolver;
}

export interface BizyKitRendererProps extends BizyKitReadOnlyRendererProps {
	searchGifs: BizyGifSearch;
	onOpenGiveBox: (draft: BizyGiveBoxDraft) => void;
	uploadDoodle: BizyDoodleUpload;
	wrap: BizyWrapInfo;
}

// Preview image for the user's active wrap (mirrors GiftWrapButton's selection):
// custom wraps use the uploaded image, others map to a tile asset.
function resolveWrapPreviewUrl(item: CollectibleInventoryItem): string | undefined {
	if (item.variant_data.type === 'custom') return item.variant_data.custom_image_url;
	const pattern = item.variant_data.type === 'avatar' ? 'avatar' : (item.variant_data.pattern ?? 'stripes');
	const color = item.variant_data.color ?? 'teal';
	return getCollectibleImageUrl(pattern, color);
}

/**
 * The subset of renderer handlers a read-only view needs: SPA/external link
 * navigation, analytics passthrough, and per-row user hydration. Deliberately
 * excludes gif search and the give-box hand-off (and the `useHttpClient`
 * dependency they pull in) — read-only history never drafts, picks gifs, or
 * opens the give-box, so those handlers would be inert.
 */
export function useBizyKitReadOnlyRendererProps(): BizyKitReadOnlyRendererProps {
	const navigate = useNavigate();
	const { track } = useAnalytics();

	// Internal in-app paths navigate via the SPA router; external URLs open in a
	// new tab. Mirrors the old in-renderer link handling.
	const onLinkClick = useCallback<BizyLinkHandler>(
		(href) => {
			if (!href) return;
			if (href.startsWith('/') && !href.startsWith('//')) {
				navigate(href);
				return;
			}
			window.open(href, '_blank', 'noopener,noreferrer');
		},
		[navigate]
	);

	return { onLinkClick, track, resolveUser: useBizyKitUserResolver };
}

/**
 * Supplies the environment-specific handlers the agnostic `DocumentRenderer`
 * from `@bonusly/bizy-kit` delegates to. Threaded into each Kaleidoscope call
 * site so the package owns rendering while the app owns navigation, analytics,
 * gif search, user hydration, and the give-box hand-off.
 */
export function useBizyKitRendererProps(): BizyKitRendererProps {
	const readOnlyProps = useBizyKitReadOnlyRendererProps();
	const http = useHttpClient();
	const user = useCurrentUser();
	const company = useCurrentCompany();
	const rewardDialog = useDialogControl('reward');
	const { data: collectibleReward } = usePostWrapReward();

	// Post-wrap toggle data for the recognition draft card: resolve the user's
	// active wrap into a preview + remaining uses; `onBuyWrap` opens Rewards when
	// they own none.
	const activeItem = getActiveCollectibleItem(user);
	const collectibleRewardId = collectibleReward?.id;
	const openBuyWrap = useCallback(() => {
		if (collectibleRewardId) rewardDialog.onOpen({ rewardId: collectibleRewardId, skipPostPurchase: 'true' });
	}, [collectibleRewardId, rewardDialog]);
	const wrap: BizyWrapInfo = {
		enabled: !!company?.post_wrap_enabled,
		activeWrap: activeItem
			? { previewUrl: resolveWrapPreviewUrl(activeItem), remainingUses: activeItem.remaining_uses, totalUses: DEFAULT_GIFT_WRAP_USES }
			: null,
		// Only offer "Add a gift wrap" when it can actually open Rewards.
		onBuyWrap: collectibleRewardId ? openBuyWrap : undefined,
	};

	const searchGifs = useCallback<BizyGifSearch>(
		async (query): Promise<BizyGifSearchResult> => {
			const res = await http.get('/gifs', { params: { query } });
			const provider = (res.data.provider || 'klipy') as GifProvider;
			const providerConfig = PROVIDER_CONFIG[provider] || PROVIDER_CONFIG.klipy;
			const gifs = (res.data.result as { id: string }[]).map((item) => {
				const gif = new Gif(item.id, item as never);
				return {
					id: gif.id,
					preview: gif.preview,
					full: gif.gif,
					mp4: gif.mp4,
					description: gif.content_description,
					attachment: gif.toAttachment().toJSON() as unknown as Record<string, unknown>,
				};
			});
			return { gifs, providerName: providerConfig.name, providerUrl: providerConfig.url };
		},
		[http]
	);

	// Legacy draft_card "Edit in give box" hand-off: build the give-box editor
	// content from the structured draft and fire the global open event.
	const onOpenGiveBox = useCallback((draft: BizyGiveBoxDraft) => {
		const hashtags = draft.hashtags?.map((h) => (h.startsWith('#') ? h.slice(1) : h));
		const editorContent = createDraftContent(draft.recipients ?? [], draft.message, draft.amount, hashtags, { aiGenerated: true });
		emit(GiveBoxEvent.Open, { editorContent });
	}, []);

	// Doodle upload: rasterized PNG → hosted URL. Uses the SAME `/bonus_images`
	// endpoint the give-box image upload (and thus Frederick's in-place doodle's
	// `onUpload`) hits, so the doodle lands as a normal bonus image attachment.
	const uploadDoodle = useCallback<BizyDoodleUpload>(
		async (png) => {
			const form = new FormData();
			form.append('file', png);
			const res = await http.post('/bonus_images', form);
			const imagePath = res.data?.result?.image_path;
			// Defense-in-depth at the seam boundary: the URL flows straight into an
			// attachment image block, so reject anything that isn't an http(s) URL
			// or a root-relative path rather than handing a junk/unsafe value on.
			if (typeof imagePath !== 'string' || !isSafeImageUrl(imagePath)) {
				throw new Error('Doodle upload returned an invalid image_path.');
			}
			return { url: imagePath };
		},
		[http]
	);

	return { ...readOnlyProps, searchGifs, onOpenGiveBox, uploadDoodle, wrap };
}
